
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { type Feedback, type FeedbackStatus } from "@/types";

export const useFeedbackData = (companyId: string | undefined) => {
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [companyStatuses, setCompanyStatuses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (companyId) {
      console.log("useFeedbackData - Fetching data for company:", companyId);
      fetchFeedback();
      fetchCompanyStatuses();
    } else {
      console.log("useFeedbackData - No company ID provided");
      setFeedback([]);
      setCompanyStatuses([]);
      setLoading(false);
    }
  }, [companyId]);

  const fetchCompanyStatuses = async () => {
    if (!companyId) return;

    try {
      console.log("Fetching company statuses for:", companyId);
      const { data: statusesData, error } = await supabase
        .from('company_statuses')
        .select('*')
        .eq('company_id', companyId)
        .order('position', { ascending: true });

      if (error) {
        console.error('Error fetching company statuses:', error);
        return;
      }

      console.log("Fetched statuses:", statusesData?.length || 0);
      if (statusesData) {
        setCompanyStatuses(statusesData);
      }
    } catch (error) {
      console.error('Error in fetchCompanyStatuses:', error);
    }
  };

  const fetchFeedback = async () => {
    if (!companyId) return;
    
    setLoading(true);
    try {
      console.log("Fetching feedback for company:", companyId);
      const { data: feedbackData, error } = await supabase
        .from('feedback')
        .select(`
          id,
          title,
          description,
          status,
          board_id,
          roadmap_id,
          created_at,
          company_id,
          score_method,
          priority_score,
          value_score,
          effort_score,
          reach_score,
          impact_score,
          confidence_score,
          effort_rice_score,
          feedback_votes (
            user_id
          )
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching feedback:', error);
        setFeedback([]);
        return;
      }

      console.log("Fetched feedback items:", feedbackData?.length || 0);
      if (feedbackData) {
        // Determine the default status to use if needed
        let defaultStatus: FeedbackStatus = 'in_review';
        if (companyStatuses.length > 0) {
          defaultStatus = companyStatuses[0].id;
        }

        const formattedFeedback = feedbackData.map(item => ({
          id: item.id,
          title: item.title,
          description: item.description,
          status: (item.status || defaultStatus) as FeedbackStatus,
          board_id: item.board_id,
          roadmap_id: item.roadmap_id,
          created_at: item.created_at,
          company_id: item.company_id,
          score_method: (item.score_method as 'value_effort' | 'rice' | undefined) || 'value_effort',
          priority_score: item.priority_score,
          value_score: item.value_score,
          effort_score: item.effort_score,
          reach_score: item.reach_score,
          impact_score: item.impact_score,
          confidence_score: item.confidence_score,
          effort_rice_score: item.effort_rice_score,
          votes: item.feedback_votes.length,
          hasVoted: item.feedback_votes.some(vote => vote.user_id === companyId)
        }));
        setFeedback(formattedFeedback);
      } else {
        setFeedback([]);
      }
    } catch (error) {
      console.error('Error in fetchFeedback:', error);
      setFeedback([]);
    } finally {
      setLoading(false);
    }
  };

  return { 
    feedback, 
    companyStatuses,
    loading,
    refetchFeedback: fetchFeedback,
    refetchCompanyStatuses: fetchCompanyStatuses
  };
};
